import React, { useRef, useCallback, useEffect, useState } from "react";
import { Container, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";

/**
 * PopiVolumeBar - Interactive volume slider component
 *
 * Features:
 * - Draggable handle for smooth volume adjustment
 * - Responsive sizing based on buttonConfig
 *
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} value - Current volume value (0-1)
 * @param {function} onChange - Callback when volume changes
 * @param {object} buttonConfig - Responsive button configuration from useDimensions
 */
const VolumeBar = ({
  x = 0,
  y = 0,
  value = 0.5,
  onChange,
  buttonConfig,
}) => {
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const backgroundRef = useRef(null);
  const foregroundRef = useRef(null);
  const maskRef = useRef(null);

  // State for dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const initialValueRef = useRef(0);

  // Get textures
  const backgroundTexture = PIXI.Assets.get(
    ASSET_NAME.popi_volume_bar_background
  );
  const foregroundTexture = PIXI.Assets.get(
    ASSET_NAME.popi_volume_bar_foreground
  );
  const buttonTexture = PIXI.Assets.get(ASSET_NAME.popi_volume_bar_button);

  // Calculate dimensions based on background texture and buttonConfig
  const getScaledDimensions = useCallback(() => {
    if (!backgroundTexture || !buttonConfig) {
      return { scale: 1, trackWidth: 300, height: 50 };
    }

    const textureWidth = backgroundTexture.width;
    const textureHeight = backgroundTexture.height;

    // Use buttonConfig.width for responsive sizing (5% larger)
    const desiredWidth = buttonConfig.width * 5.5;
    const scale = desiredWidth / textureWidth;

    return {
      scale,
      trackWidth: desiredWidth,
      height: textureHeight * scale,
    };
  }, [backgroundTexture, buttonConfig]);

  const dimensions = getScaledDimensions();

  // Calculate handle position based on value (0-1)
  const getHandlePosition = useCallback(
    (vol) => {
      // Handle moves across full track width (0 to trackWidth)
      return dimensions.trackWidth * vol;
    },
    [dimensions]
  );

  const handleX = getHandlePosition(value);

  // Handle pointer down on handle (start dragging)
  const handlePointerDown = useCallback(
    (event) => {
      event.stopPropagation();
      setIsDragging(true);

      // Use global position from PixiJS event
      const startX = event.global?.x || event.data?.global?.x;
      dragStartXRef.current = startX;
      initialValueRef.current = value;
    },
    [value]
  );

  // Handle pointer move (dragging)
  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (event) => {
      // Use clientX for native DOM events
      const currentX = event.clientX || event.touches?.[0]?.clientX;
      if (currentX === undefined) return;

      const deltaX = currentX - dragStartXRef.current;
      const deltaValue = deltaX / dimensions.trackWidth;
      const newValue = Math.max(
        0,
        Math.min(1, initialValueRef.current + deltaValue)
      );

      if (onChange) {
        onChange(newValue);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove);
    window.addEventListener("touchend", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, [isDragging, onChange, dimensions]);

  // Don't render if textures not loaded
  if (!backgroundTexture || !foregroundTexture || !buttonTexture) {
    return null;
  }

  return (
    <Container ref={containerRef} name="secondary_volume_bar" x={x} y={y}>
      {/* Background track */}
      <Sprite
        ref={backgroundRef}
        texture={backgroundTexture}
        name="volume_bar_background"
        anchor={{ x: 0, y: 0.5 }}
        width={dimensions.trackWidth}
        height={dimensions.height}
      />

      {/* Foreground (filled portion) - masked to show only up to handle position */}
      <Container name="foreground_container">
        <Graphics
          ref={maskRef}
          draw={(g) => {
            g.clear();
            g.beginFill(0xffffff);
            g.drawRect(0, -dimensions.height / 2, handleX, dimensions.height);
            g.endFill();
          }}
        />
        <Sprite
          ref={foregroundRef}
          texture={foregroundTexture}
          name="volume_bar_foreground"
          anchor={{ x: 0, y: 0.5 }}
          width={dimensions.trackWidth}
          height={dimensions.height}
          mask={maskRef.current}
        />
      </Container>

      {/* Draggable handle */}
      <Sprite
        ref={handleRef}
        texture={buttonTexture}
        name="volume_bar_handle"
        anchor={0.5}
        x={handleX}
        y={0}
        width={dimensions.height * 1.2}
        height={dimensions.height * 1.2}
        eventMode="static"
        cursor={isDragging ? "grabbing" : "grab"}
        onpointerdown={handlePointerDown}
      />
    </Container>
  );
};

export default VolumeBar;
